import React from 'react';
import Link from 'next/link';
import { ISnowWebcam } from '@/api/home/types';
import { IResortPathInfo } from '@/api/resort/types';
import { Routes } from '@/routes';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { Heading } from '@/components/ui/heading';
import { ResortWebcam } from '@/components/common/ResortWebcam';
import { formatAgoTimestampOrString } from '@/util/formatAgo';
import { cn } from '@/util/cn';

type HeaderScope = 'h1' | 'h2';

const headerClass = 'mb-4 flex flex-wrap items-end justify-between gap-3';

const titleClass = 'font-heading text-xl text-secondary md:text-2xl';

const viewAllClass = 'text-sm font-semibold text-primary hover:underline';

export const SnowWebcams = ({
  snowWebcams,
  header,
  imageLink,
  titleLink,
  headerScope = 'h2',
  hasMostReportedSnowfall = false,
  currentRegionSlug,
}: {
  headerScope?: HeaderScope;
  snowWebcams: Array<ISnowWebcam>,
  header?: string,
  showSummitDepth?: boolean;
  showSnowReport?: boolean;
  imageLink?: (r: IResortPathInfo) => string,
  titleLink?: (r: IResortPathInfo) => string,
  hasMostReportedSnowfall?: boolean,
  currentRegionSlug?: string,
}) => {
  const { t } = useOTSTranslation('common');

  const maxCameraCount = hasMostReportedSnowfall ? 4 : 6;
  const cameras = snowWebcams.slice(0, maxCameraCount);

  // @ts-ignore
  const camsUrl = currentRegionSlug ? Routes.regionCams({ slug: currentRegionSlug }) : undefined;

  if (cameras.length === 0) return null;

  return (
    <section className="max-lg:mt-4">
      {header && (
        <div className={headerClass}>
          <Heading level={headerScope === 'h1' ? 1 : 2} className={titleClass}>
            {header}
          </Heading>
          {camsUrl && (
            <Link href={camsUrl} className={viewAllClass}>
              {t('homePage.viewCamsLabel')}
            </Link>
          )}
        </div>
      )}
      <ul
        className={cn(
          'grid gap-4',
          hasMostReportedSnowfall ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        )}
      >
        {cameras.map(({ webcam, resort }) => {
          const link = imageLink?.(resort) ?? titleLink?.(resort);
          return (
            <li key={webcam.uuid}>
              <ResortWebcam
                caption={webcam.title}
                resortName={resort.title}
                showResort
                image={webcam.image}
                timestamp={webcam.date ? formatAgoTimestampOrString(webcam.date) : undefined}
                href={link}
              />
            </li>
          );
        })}
      </ul>
    </section>
  );
};
